import { Autocomplete, TextField } from "@mui/material";
import { fetchCustomerCountries, fetchCustomerLeasingCountries } from "actions/customerActions";
import clsx from "clsx";
import { Country, getCode, getData } from "country-list";
import {
	selectCustomerCountries,
	selectCustomerCountriesKeyedByCountryCode,
	selectCustomerLeasingCountries,
} from "features/customer";
import { useApiOnce, useAppSelector } from "hooks/hooks";
import { useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { TruncatableTypography } from "../TruncateableTypography";
import styles from "./CountrySelector.module.scss";

interface CountryFlagProps {
	country?: string;
	countryCode?: string;
	coloredBackground: boolean;
}

// Your existing CountryFlag component
const CountryFlag = ({ country, countryCode, coloredBackground }: CountryFlagProps) => {
	const code = country ? getCode(country ?? "N/A") : countryCode;
	if (!code) {
		return null;
	}
	return (
		<span className={clsx(styles.flag, { [styles.coloredBackground]: coloredBackground })}>
			<img src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`} alt={code} />
		</span>
	);
};

interface CountrySelectorProps {
	value: string;
	register: any;
	isLeasing?: boolean;
	setValue?: UseFormSetValue<FieldValues>;
}

const CountrySelector = ({ value, register, isLeasing, setValue }: CountrySelectorProps) => {
	const countriesData = getData();

	const customerLeasingCountries = useAppSelector(selectCustomerLeasingCountries);
	useApiOnce(fetchCustomerLeasingCountries, customerLeasingCountries);

	useApiOnce(fetchCustomerCountries, useAppSelector(selectCustomerCountries));
	const customerCountryByKey = useAppSelector(selectCustomerCountriesKeyedByCountryCode);

	// Needed to stop MUI from complaining about empty country objects on users
	const emptyCountry = useMemo(() => null as Country | null, []);

	const sortedAndPrioritized = useMemo(() => {
		const data = isLeasing
			? customerLeasingCountries.countries.map(({ country, countryCode }) => ({
					name: country,
					code: countryCode,
				}))
			: countriesData;
		const sorted = data.sort((a, b) => {
			const numEmployeesOnCountryA = customerCountryByKey[a.code]?.numberOfEmployees ?? 0;
			const numEmployeesOnCountryB = customerCountryByKey[b.code]?.numberOfEmployees ?? 0;

			if (a.name === "") {
				return -1;
			} // Empty country should always be first
			if (numEmployeesOnCountryA > numEmployeesOnCountryB) {
				return -1;
			}
			if (numEmployeesOnCountryA < numEmployeesOnCountryB) {
				return 1;
			}
			return 0;
		});
		return sorted;
	}, [countriesData, customerCountryByKey, isLeasing, customerLeasingCountries]);

	const selectedCountry = countriesData.find((country) => country.name === value) ?? emptyCountry;

	const [currentSelectedCountry, setCurrentSelectedCountry] = useState<Country | null>(
		selectedCountry,
	);

	// Update currentSelectedCountry when value prop changes
	useEffect(() => {
		const newSelectedCountry =
			countriesData.find((country) => country.name === value) ?? emptyCountry;
		setCurrentSelectedCountry(newSelectedCountry);
	}, [value, countriesData, emptyCountry]);

	return (
		<Autocomplete
			value={currentSelectedCountry}
			options={sortedAndPrioritized}
			disabled={isLeasing && customerLeasingCountries.countries.length === 0}
			onChange={(_, newValue: any) => {
				setValue && setValue("country", newValue?.name);
				setCurrentSelectedCountry(newValue ?? {});
			}}
			isOptionEqualToValue={(option, value) => option?.name === value?.name}
			getOptionLabel={(option) => option.name}
			renderOption={(props, option) => (
				<li {...props} key={option.code}>
					<CountryFlag country={option.name} coloredBackground />
					<TruncatableTypography variant="body1" maxCharLength={30} ml={1.5}>
						{option.name}
					</TruncatableTypography>
				</li>
			)}
			renderInput={(params) => (
				<>
					<TextField
						{...params}
						placeholder="Select a country"
						className={styles.autcompleteInput}
						InputProps={{
							style: {
								height: 40,
								display: "flex",
								alignContent: "center",
							},
							...params.InputProps,
						}}
					/>
					{/* Hidden input to register the selected country */}
					<input
						type="hidden"
						value={currentSelectedCountry?.name || ""}
						{...register("country")}
					/>
				</>
			)}
		/>
	);
};

export { CountryFlag, CountrySelector };
